import { useState, useEffect } from 'react'
import { Dialog, DialogPortal } from '@/components/ui/dialog'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Button } from '@/components/ui/button'
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react'
import { format, isSameDay, isBefore, startOfToday, eachDayOfInterval, subMonths, addMonths, startOfMonth, endOfMonth, isSameMonth, isToday, addDays } from 'date-fns'
import { cn } from '@/lib/utils'

// Date Selection Dialog Component
interface DateSelectionDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  selectedDates: Date[]
  onDateSelect: (date: Date) => void
  onDateRangeSelect: (startDate: Date, endDate: Date) => void
  title?: string
  singleDateOnly?: boolean
}

export function DateSelectionDialog({
  isOpen,
  onOpenChange,
  selectedDates,
  onDateSelect,
  onDateRangeSelect,
  title = 'Select Dates',
  singleDateOnly = false,
}: DateSelectionDialogProps) {
  const [currentDate, setCurrentDate] = useState(selectedDates[0] || new Date())
  const [tempSelectedDates, setTempSelectedDates] = useState<Date[]>(selectedDates)

  useEffect(() => {
    if (isOpen) {
      setTempSelectedDates(selectedDates)
    }
  }, [isOpen, selectedDates])

  const handleDayClick = (date: Date) => {
    if (singleDateOnly) {
      // Single date selection only
      if (tempSelectedDates.length === 1 && isSameDay(date, tempSelectedDates[0])) {
        // Deselect if clicking the same date
        setTempSelectedDates([])
      } else {
        // Select the new date
        setTempSelectedDates([date])
      }
    } else {
      // Original range selection logic
      if (tempSelectedDates.length === 0) {
        // First date selected
        setTempSelectedDates([date])
      } else if (isSameDay(date, tempSelectedDates[0]) && tempSelectedDates.length === 1) {
        // Clicking the only selected date - deselect
        setTempSelectedDates([])
      } else if (isSameDay(date, tempSelectedDates[tempSelectedDates.length - 1]) && tempSelectedDates.length > 1) {
        // Clicking the end date - just remove this day
        const newDates = tempSelectedDates.slice(0, -1)
        setTempSelectedDates(newDates)
      } else if (isSameDay(date, tempSelectedDates[0]) && tempSelectedDates.length > 1) {
        // Clicking the start date - deselect all
        setTempSelectedDates([])
      } else if (tempSelectedDates.some(d => isSameDay(d, date))) {
        // Clicking a date in the middle - deselect all
        setTempSelectedDates([])
      } else {
        // Check if the clicked date is adjacent to either end of the range
        const lastDate = tempSelectedDates[tempSelectedDates.length - 1]
        const firstDate = tempSelectedDates[0]
        const isAdjacentToEnd = isSameDay(addDays(lastDate, 1), date)
        const isAdjacentToStart = isSameDay(addDays(firstDate, -1), date)

        if (isAdjacentToEnd) {
          // Extending forward - check 30-day limit
          const dates = eachDayOfInterval({ start: firstDate, end: date })
          if (dates.length <= 31) { // 31 days including both start and end
            setTempSelectedDates(dates)
          }
        } else if (isAdjacentToStart) {
          // Extending backward - check 30-day limit
          const dates = eachDayOfInterval({ start: date, end: lastDate })
          if (dates.length <= 31) {
            setTempSelectedDates(dates)
          }
        } else if (tempSelectedDates.length === 1) {
          // Only one date selected - fill the range between
          const singleDate = tempSelectedDates[0]
          let dates: Date[]
          if (isBefore(date, singleDate)) {
            dates = eachDayOfInterval({ start: date, end: singleDate })
          } else {
            dates = eachDayOfInterval({ start: singleDate, end: date })
          }
          // Check 30-day limit
          if (dates.length <= 31) {
            setTempSelectedDates(dates)
          }
        }
        // If not adjacent and more than one date, do nothing (can't select non-consecutive dates)
      }
    }
  }

  const handleSave = () => {
    if (tempSelectedDates.length === 1) {
      onDateSelect(tempSelectedDates[0])
    } else if (tempSelectedDates.length > 1) {
      onDateRangeSelect(tempSelectedDates[0], tempSelectedDates[tempSelectedDates.length - 1])
    }
    onOpenChange(false)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-[120] bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-0 right-0 top-[50%] z-[120] translate-y-[-50%] border-none bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] rounded-[var(--card-radius)] max-h-[90vh] p-0 overflow-hidden mx-auto",
            "max-w-[calc(var(--max-content-width)+var(--min-side-padding)*2)]"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-4">
            <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              {title}
            </h2>
          </div>

          {/* Content */}
          <div className="px-6 pb-6">
            <div className="space-y-5">
              {/* Calendar */}
              <DatePickerWithRangeSelection
                currentDate={currentDate}
                onCurrentDateChange={setCurrentDate}
                selectedDates={tempSelectedDates}
                onDayClick={handleDayClick}
              />

              {/* Action Buttons */}
              <div className="flex flex-col items-center gap-3 pt-4">
                <Button
                  onClick={handleSave}
                  disabled={tempSelectedDates.length === 0}
                  className="w-full px-8 py-6 text-base font-medium rounded-[var(--card-radius)]"
                  size="lg"
                >
                  {singleDateOnly ? 'Select Date' : 'Confirm Dates'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="w-full px-8 py-6 text-base font-medium rounded-[var(--card-radius)]"
                  size="lg"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  )
}

// Calendar component with range selection
interface DatePickerWithRangeSelectionProps {
  currentDate: Date
  onCurrentDateChange: (date: Date) => void
  selectedDates: Date[]
  onDayClick: (date: Date) => void
}

export function DatePickerWithRangeSelection({
  currentDate,
  onCurrentDateChange,
  selectedDates,
  onDayClick,
}: DatePickerWithRangeSelectionProps) {
  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd })

  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  const startPadding = monthStart.getDay() === 0 ? 6 : monthStart.getDay() - 1
  const paddingDays = Array.from({ length: startPadding }, () => null)

  const isSelected = (date: Date) => selectedDates.some(d => isSameDay(d, date))

  return (
    <div>
      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => onCurrentDateChange(subMonths(currentDate, 1))}
          className="p-2 rounded-full hover:bg-muted"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>

        <h3 className="text-base font-medium">{format(currentDate, 'MMMM yyyy')}</h3>

        <button
          onClick={() => onCurrentDateChange(addMonths(currentDate, 1))}
          className="p-2 rounded-full hover:bg-muted"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {/* Day names */}
        {dayNames.map(day => (
          <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
            {day}
          </div>
        ))}

        {/* Padding days */}
        {paddingDays.map((_, i) => (
          <div key={`pad-${i}`} className="aspect-square" />
        ))}

        {/* Calendar days */}
        {daysInMonth.map(date => {
          const isDateSelected = isSelected(date)
          const isPast = isBefore(date, startOfToday())

          return (
            <button
              key={date.toISOString()}
              onClick={() => !isPast && onDayClick(date)}
              disabled={isPast}
              className={cn(
                'aspect-square rounded-md flex items-center justify-center text-sm font-medium transition-all',
                !isSameMonth(date, currentDate) && 'text-muted-foreground opacity-50',
                isPast && 'opacity-40 cursor-not-allowed',
                isDateSelected && 'bg-primary text-primary-foreground',
                !isDateSelected && !isPast && 'hover:bg-muted',
                isToday(date) && !isDateSelected && 'border border-primary',
              )}
            >
              {format(date, 'd')}
            </button>
          )
        })}
      </div>
    </div>
  )
}

